import { useFormikContext } from 'formik';
import React, { useState, useRef, useEffect } from 'react';

const hmdColors = [
  { name: 'Cyan', hex: '00FFFF', rgb: '0, 255, 255' },
  { name: 'Teal', hex: '008080', rgb: '0, 128, 128' },
  { name: 'Deep Pink', hex: 'FF1493', rgb: '255, 20, 147' },
  { name: 'Blue', hex: '0000FF', rgb: '0, 0, 255' },
  { name: 'Orange', hex: 'FFA500', rgb: '255, 165, 0' },
  { name: 'Purple', hex: '800080', rgb: '128, 0, 128' },
  { name: 'Olive', hex: '808000', rgb: '128, 128, 0' },
  { name: 'Pink', hex: 'FFC0CB', rgb: '255, 192, 203' },
  { name: 'Yellow-Green', hex: '7FFF00', rgb: '127, 255, 0' },
  { name: 'Blue-Green', hex: '00FF7F', rgb: '0, 255, 127' },
  { name: 'Blue-Purple', hex: '4B0082', rgb: '75, 0, 130' },
  { name: 'Maroon', hex: '800000', rgb: '128, 0, 0' },
];

const HmdColorPicker = ({ defaultValue, onColorSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(defaultValue || '00FFFF');
  const dropdownRef = useRef(null);
  const {
    errors,
    touched,
  } = useFormikContext();

  // Find color name by hex code
  const getColorName = hexCode => {
    const color = hmdColors.find(c => c.hex === hexCode);
    return color ? color.name : '';
  };

  // Handle color selection
  const handleColorSelect = color => {
    setSelectedColor(color.hex);
    setIsOpen(false);

    if (onColorSelect) {
      onColorSelect({ hex: color.hex, name: color.name });
    }
  };

  // Handle clicks outside of dropdown to close it
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get currently selected color object
  const selectedColorObj =
    hmdColors.find(c => c.hex === selectedColor) || hmdColors[0];

  return (
    <div className="hmd-color-picker w-100" ref={dropdownRef}>
      <div
        className={`color-selector form-control ${errors?.color && touched?.color ? 'is-invalid' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div
          className="color-box"
          style={{ backgroundColor: `#${selectedColor}` }}
        ></div>
        <div className="color-name">{getColorName(selectedColor)}</div>
        <div className="dropdown-arrow">{isOpen ? '▲' : '▼'}</div>
      </div>

      {isOpen && (
        <div className="color-dropdown">
          {hmdColors.map((color, index) => (
            <div
              key={index}
              className={`color-option ${
                selectedColor === color.hex ? 'selected' : ''
              }`}
              onClick={() => handleColorSelect(color)}
            >
              <div
                className="color-box"
                style={{ backgroundColor: `#${color.hex}` }}
              ></div>
              <div className="color-label">{color.name}</div>
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        .hmd-color-picker {
          position: relative;
          width: 250px;
        }

        .color-selector {
          display: flex;
          align-items: center;
          border: 1px solid #ccc;
          padding: 8px;
          cursor: pointer;
          height: 38px;
        }

        .color-box {
          width: 20px;
          height: 20px;
          border: 1px solid #ddd;
          margin-right: 10px;
        }

        .color-name {
          flex: 1;
        }

        .dropdown-arrow {
          padding: 0 8px;
          color: #666;
        }

        .color-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          width: 100%;
          max-height: 250px;
          overflow-y: auto;
          border: 1px solid #ccc;
          border-top: none;
          background: white;
          z-index: 100;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .color-option {
          display: flex;
          align-items: center;
          padding: 8px;
          cursor: pointer;
        }

        .color-option:hover {
          background-color: #f5f5f5;
        }

        .color-option.selected {
          background-color: #e6f7ff;
        }

        .color-label {
          margin-left: 10px;
        }
      `}</style>
    </div>
  );
};

export default HmdColorPicker;
